import { useCallback, useEffect, useMemo, useState } from 'react';
import { generatePath } from 'react-router';

import { ExceptionsApi } from 'Api/Exceptions/ExceptionsApi';
import { IssuesApi } from 'Api/Issues/IssuesApi';
import { DueDiligenceAssessmentConfigurationApi } from 'Api/TPRM/DueDiligenceAssessmentConfigurationApi';
import { ClientTPRMApi } from 'Api/TPRM/TPRMApi';
import { Link } from 'Components/Buttons/Buttons';
import { FormFieldSelect } from 'Components/FormField/FormFieldSelect/FormFieldSelect';
import { PageLayoutDefault } from 'Components/PageLayout/PageLayoutDefault';
import { Placeholder } from 'Components/Placeholder/Placeholder';
import { ThirdPartyContactModal } from 'Components/TPRM/ThirdPartyContactModal/ThirdPartyContactModal';
import { Text } from 'Components/Text/Text';
import { DueDiligenceWorkflowDueDateModal } from 'Components/UcrmTprm/DueDiligenceWorkflowDueDateModal/DueDiligenceWorkflowDueDateModal';
import { IrqTargetCompletionDateModal } from 'Components/UcrmTprm/IrqTargetCompletionDateModal/IrqTargetCompletionDateModal';
import { SetActiveStatesModal } from 'Components/UcrmTprm/SetActiveStatesModal/SetActiveStatesModal';
import { UsersModal } from 'Components/UcrmTprm/UsersModal/UsersModal';
import { GENERIC_ERROR_MESSAGE } from 'Config/Errors';
import { ICON_ADD_CREATE } from 'Config/Icons';
import { TPRM_INTAKE } from 'Config/PathTemplates';
import { ASSESSMENTS_IN_PROGRESS, ASSESSMENTS_SUBMITTED, FINAL_REVIEW_IN_PROGRESS, IRQ_IN_PROGRESS, IRQ_NOT_FINALIZED, QUESTIONNAIRES_IN_PROGRESS, TPRM_QUESTIONNAIRES_SUBMITTED, TPRM_SERVICE_NOT_SUBMITTED_TO_BE_FINALIZED, TPRM_SERVICE_PENDING_FINALIZATION, TPRM_STEPS_FOR_NON_TPRM_USER, TPRM_STEPS_FOR_TPRM_USER, TPRM_THIRD_PARTY_NOT_SUBMITTED_TO_BE_FINALIZED, TPRM_THIRD_PARTY_PENDING_FINALIZATION, WORKFLOW_NOT_STARTED } from 'Config/Text';
import { TPRM_LISTING_ACTIVE_STATE_FILTER } from 'Config/Tooltips';
import { AuthHandler } from 'Helpers/Auth/AuthHandler';
import { loggedInUserHasFullAccessToTprm } from 'Helpers/UserUtils';
import { DisplayedModal, OrganizationsAndAssessmentSubjectsTableRowData, UserTypeAndOrganizations } from 'Models/AssessmentSubject';
import { AssessmentWorkflowState, DueDiligenceWorkflowProgress } from 'Models/DueDiligenceWorkflow';
import { FolderVersion } from 'Models/Folders';
import { ActiveState, ActiveStateDisplayOption, FinalizationState, activeStateFilterOptions } from 'Models/Organization';
import { ProductModule } from 'Models/ProductModule';
import { ServiceForNonTprmUser, ThirdPartyContact, ThirdPartyResponseWithServices, ThirdPartyService, ThirdPartyWithServicesForNonTprmUser, TprmEntities } from 'Models/TPRM';

import styles from './ThirdPartiesAndServicesListing.module.css';
import { ThirdPartiesAndServicesTable, ThirdPartiesAndServicesTableProps } from './ThirdPartiesAndServicesTable/ThirdPartiesAndServicesTable';
import { DeleteThirdPartyModal } from '../ManageThirdParty/Components/DeleteThirdPartyModal/DeleteThirdPartyModal';
import { DeleteThirdPartyServiceModal } from '../ManageThirdPartyService/Components/DeleteThirdPartyServiceModal/DeleteThirdPartyServiceModal';

export interface ThirdPartiesAndServicesListingProps {
    authHandler: AuthHandler;
    dueDiligenceAssessmentConfigurationApi: DueDiligenceAssessmentConfigurationApi;
    exceptionsApi: ExceptionsApi;
    issuesApi: IssuesApi;
    tprmApi: ClientTPRMApi;
}

/** The modals that this page displays: those shared with the UCRM listing page, plus third-party contacts. */
type TprmDisplayedModal = DisplayedModal<TprmEntities> | { type: 'contacts'; contacts: ThirdPartyContact[]; header: string };

export const ThirdPartiesAndServicesListing = (props: ThirdPartiesAndServicesListingProps) => {
    const [activeStateDisplayOption, setActiveStateDisplayOption] = useState<ActiveStateDisplayOption>(ActiveStateDisplayOption.ACTIVE_ONLY);
    const [displayedModal, setDisplayedModal] = useState<TprmDisplayedModal>({ type: 'none' });
    const [userTypeAndThirdParties, setUserTypeAndThirdParties] = useState<UserTypeAndOrganizations<TprmEntities>>();
    const [folders, setFolders] = useState<FolderVersion[] | null>();

    const [loadingErrorOccurred, setLoadingErrorOccurred] = useState(false);

    const getThirdParties = useCallback(async (): Promise<void> => {
        try {
            const userRoles = props.authHandler.getUserRoles();
            const userId = props.authHandler.getAuthenticatedUserSubject()!;
            const userHasFullTprmAccess = loggedInUserHasFullAccessToTprm(userRoles);

            if (userHasFullTprmAccess) {
                const thirdParties = (await props.tprmApi.getThirdParties()).data;
                setUserTypeAndThirdParties({ userHasFullAccess: true, userId, organizations: thirdParties });
            } else {
                const thirdParties = (await props.tprmApi.getThirdPartiesForNonTprmUser()).data;
                setUserTypeAndThirdParties({ userHasFullAccess: false, userId, organizations: thirdParties });
            }
        } catch (error) {
            console.error(error);
            setLoadingErrorOccurred(true);
        }
    }, [props.authHandler, props.tprmApi]);

    const getFolders = useCallback(async (): Promise<void> => {
        try {
            const userRoles = props.authHandler.getUserRoles();
            const userHasFullTprmAccess = loggedInUserHasFullAccessToTprm(userRoles);

            if (userHasFullTprmAccess) {
                const response = await props.tprmApi.getAllCurrentFolders();
                setFolders(response.data);
            } else {
                setFolders(null);
            }
        } catch (error) {
            console.error(error);
            setLoadingErrorOccurred(true);
        }
    }, [props.authHandler, props.tprmApi]);

    useEffect(() => {
        getThirdParties();
        getFolders();
    }, [getFolders, getThirdParties]);

    const servicesById: Map<string, ThirdPartyService | ServiceForNonTprmUser> | undefined = useMemo(() => {
        if (!userTypeAndThirdParties) {
            return undefined;
        }

        return new Map(userTypeAndThirdParties.organizations.flatMap((thirdParty) => thirdParty.services).map((service) => [service.id, service]));
    }, [userTypeAndThirdParties]);

    const foldersByThirdPartyId: Map<string, FolderVersion[]> | undefined = useMemo(() => {
        if (folders === undefined) {
            return undefined;
        }

        const _foldersByThirdPartyId = new Map<string, FolderVersion[]>();

        if (folders === null) {
            return _foldersByThirdPartyId;
        }

        folders.forEach((folder) => {
            const foldersForThirdParty = _foldersByThirdPartyId.get(folder.third_party_id);
            if (foldersForThirdParty) {
                foldersForThirdParty.push(folder);
            } else {
                _foldersByThirdPartyId.set(folder.third_party_id, [folder]);
            }
        });

        return _foldersByThirdPartyId;
    }, [folders]);

    const tableRowsData: OrganizationsAndAssessmentSubjectsTableRowData<TprmEntities>[] | undefined = useMemo(() => {
        if (!(userTypeAndThirdParties && foldersByThirdPartyId !== undefined)) {
            return undefined;
        }

        const getCommonAssessmentsParentService = (service: ThirdPartyService | ServiceForNonTprmUser): ThirdPartyService | ServiceForNonTprmUser | undefined => (service.assessment_workflow_setup.common_assessment_parent ? servicesById?.get(service.assessment_workflow_setup.common_assessment_parent.id) : undefined);

        const userId = userTypeAndThirdParties.userId;
        const _dataForRows: OrganizationsAndAssessmentSubjectsTableRowData<TprmEntities>[] = [];

        userTypeAndThirdParties.organizations.forEach((thirdParty) => {
            const thirdPartyMatchesActiveStateDisplayOption = (() => {
                switch (activeStateDisplayOption) {
                    case ActiveStateDisplayOption.ALL:
                        return true;
                    case ActiveStateDisplayOption.ACTIVE_ONLY:
                        return thirdParty.active_state === ActiveState.ACTIVE;
                    case ActiveStateDisplayOption.INACTIVE_ONLY:
                        return thirdParty.active_state === ActiveState.INACTIVE || thirdParty.services.some((service) => service.active_state === ActiveState.INACTIVE);
                }
            })();

            const servicesMatchingActiveStateDisplayOption = thirdParty.services.filter((service) => {
                return (() => {
                    switch (activeStateDisplayOption) {
                        case ActiveStateDisplayOption.ALL:
                            return true;
                        case ActiveStateDisplayOption.ACTIVE_ONLY:
                            return service.active_state === ActiveState.ACTIVE;
                        case ActiveStateDisplayOption.INACTIVE_ONLY:
                            return service.active_state === ActiveState.INACTIVE;
                    }
                })();
            });

            if (servicesMatchingActiveStateDisplayOption.length === 0) {
                if (thirdPartyMatchesActiveStateDisplayOption) {
                    if (userTypeAndThirdParties.userHasFullAccess) {
                        _dataForRows.push({ userHasFullAccess: true, userId, foldersByOrganizationId: foldersByThirdPartyId, organization: thirdParty as ThirdPartyResponseWithServices });
                    } else {
                        _dataForRows.push({ userHasFullAccess: false, userId, organization: thirdParty as ThirdPartyWithServicesForNonTprmUser });
                    }
                }
            } else {
                servicesMatchingActiveStateDisplayOption.forEach((service) => {
                    if (userTypeAndThirdParties.userHasFullAccess) {
                        const _thirdParty = thirdParty as ThirdPartyResponseWithServices;
                        const _service = service as ThirdPartyService;
                        const assessmentSubjectData = {
                            assessmentSubject: _service,
                            dueDiligenceWorkflowProgress: getDueDiligenceWorkflowProgressForService(_thirdParty, _service, getCommonAssessmentsParentService),
                        };
                        _dataForRows.push({ userHasFullAccess: true, userId, foldersByOrganizationId: foldersByThirdPartyId, organization: _thirdParty, assessmentSubjectData });
                    } else {
                        const _thirdParty = thirdParty as ThirdPartyWithServicesForNonTprmUser;
                        const _service = service as ServiceForNonTprmUser;
                        const assessmentSubjectData = {
                            assessmentSubject: _service,
                            dueDiligenceWorkflowProgress: getDueDiligenceWorkflowProgressForServiceForNonTprmUser(_thirdParty, _service, getCommonAssessmentsParentService),
                        };
                        _dataForRows.push({ userHasFullAccess: false, userId, organization: _thirdParty, assessmentSubjectData });
                    }
                });
            }
        });

        return _dataForRows;
    }, [activeStateDisplayOption, servicesById, userTypeAndThirdParties, foldersByThirdPartyId]);

    if (loadingErrorOccurred) {
        return <Text>{GENERIC_ERROR_MESSAGE}</Text>;
    }

    if (!(tableRowsData && userTypeAndThirdParties && foldersByThirdPartyId !== undefined)) {
        return <Placeholder />;
    }

    const hideModal = (): void => {
        setDisplayedModal({ type: 'none' });
    };

    const manageThirdPartiesAndServicesTableProps: ThirdPartiesAndServicesTableProps = {
        onDisplayContacts: (contacts, header) => setDisplayedModal({ type: 'contacts', contacts, header }),
        onDisplayUsers: (userIds, header) => setDisplayedModal({ type: 'users', userIds, header }),
        onDeleteThirdParty: (thirdParty) => setDisplayedModal({ type: 'deleteOrganization', organization: thirdParty }),
        onDeleteService: (service) => setDisplayedModal({ type: 'deleteAssessmentSubject', assessmentSubject: service }),
        onSetActiveStates: (thirdParty) => setDisplayedModal({ type: 'setActiveStates', organization: thirdParty }),
        onSetAssessmentDueDate: (service) => setDisplayedModal({ type: 'setAssessmentDueDate', assessmentSubject: service }),
        onSetIrqTargetCompletionDate: (service) => setDisplayedModal({ type: 'setIrqTargetCompletionDate', assessmentSubject: service }),
        userTypeAndTableRowsData: { userHasFullTprmAccess: userTypeAndThirdParties.userHasFullAccess, userId: userTypeAndThirdParties.userId, tableRowsData },
    };

    return (
        <>
            {displayedModal.type === 'contacts' && <ThirdPartyContactModal thirdPartyContacts={displayedModal.contacts} header={displayedModal.header} hideModal={hideModal} />}
            {displayedModal.type === 'users' && <UsersModal userIds={displayedModal.userIds} header={displayedModal.header} hideModal={hideModal} />}

            {displayedModal.type === 'setAssessmentDueDate' && <DueDiligenceWorkflowDueDateModal module={ProductModule.TPRM} dueDiligenceAssessmentConfigurationApi={props.dueDiligenceAssessmentConfigurationApi} assessmentSubject={displayedModal.assessmentSubject} hideModal={hideModal} moduleApi={props.tprmApi} onDueDateSet={getThirdParties} />}
            {displayedModal.type === 'setIrqTargetCompletionDate' && <IrqTargetCompletionDateModal module={ProductModule.TPRM} hideModal={hideModal} assessmentSubject={displayedModal.assessmentSubject} targetDateSet={getThirdParties} moduleApi={props.tprmApi} />}

            {displayedModal.type === 'deleteOrganization' && <DeleteThirdPartyModal exceptionsApi={props.exceptionsApi} issuesApi={props.issuesApi} onSuccess={getThirdParties} onHideModal={hideModal} thirdParty={displayedModal.organization} tprmApi={props.tprmApi} userHasFullTprmAccess={userTypeAndThirdParties.userHasFullAccess} />}
            {displayedModal.type === 'deleteAssessmentSubject' && <DeleteThirdPartyServiceModal hideModal={hideModal} onSuccess={getThirdParties} service={displayedModal.assessmentSubject} tprmApi={props.tprmApi} userHasFullTprmAccess={userTypeAndThirdParties.userHasFullAccess} />}

            {displayedModal.type === 'setActiveStates' && <SetActiveStatesModal organization={displayedModal.organization} module={ProductModule.TPRM} moduleApi={props.tprmApi} hideModal={hideModal} onActiveStatesSubmitted={getThirdParties} />}

            <PageLayoutDefault
                headerTitle="Third Parties and Services"
                headerDescription="Third parties and services are paired into rows in the table below. Filters cause a row to be shown if their selections match either the third party or the service in the row."
                headerButtons={
                    <>
                        <Link variant="primaryButton" to={generatePath(TPRM_INTAKE)} fontAwesomeImage={ICON_ADD_CREATE}>
                            Create
                        </Link>
                    </>
                }
                body={[
                    {
                        content: (
                            <>
                                <div className={styles.activeStateFilter}>
                                    <FormFieldSelect tooltip={TPRM_LISTING_ACTIVE_STATE_FILTER} formFieldId="activeStateFilter" formFieldLabel="Show active/inactive" options={activeStateFilterOptions} selectedOption={activeStateDisplayOption} handleChange={(value) => setActiveStateDisplayOption(value as ActiveStateDisplayOption)} notClearable />
                                </div>
                                <ThirdPartiesAndServicesTable {...manageThirdPartiesAndServicesTableProps} />
                            </>
                        ),
                    },
                ]}
            />
        </>
    );
};

export const getDueDiligenceWorkflowProgressForService = (thirdParty: ThirdPartyResponseWithServices, service: ThirdPartyService, getCommonAssessmentsParentService: (service: ThirdPartyService | ServiceForNonTprmUser) => ThirdPartyService | ServiceForNonTprmUser | undefined): DueDiligenceWorkflowProgress => {
    const steps = [...TPRM_STEPS_FOR_TPRM_USER];
    const commonAssessmentsParentService = getCommonAssessmentsParentService(service);
    const serviceWithAssessments = commonAssessmentsParentService ?? service;

    if (commonAssessmentsParentService) {
        steps[5] = `${TPRM_QUESTIONNAIRES_SUBMITTED} (shared due diligence assessments provided by ${commonAssessmentsParentService.name})`;
        steps[6] = `${ASSESSMENTS_SUBMITTED} (shared due diligence assessments provided by ${commonAssessmentsParentService.name})`;
    }

    if (service.assessment_workflow_data.state === AssessmentWorkflowState.PERFORMING_DUE_DILIGENCE) {
        if (serviceWithAssessments.assessment_workflow_data.all_assessments_completed) {
            return {
                completedStepNumber: 6,
                inProgressStepDescription: FINAL_REVIEW_IN_PROGRESS,
                steps,
            };
        } else if (serviceWithAssessments.assessment_workflow_data.all_questionnaires_completed) {
            return {
                completedStepNumber: 5,
                inProgressStepDescription: commonAssessmentsParentService ? `${ASSESSMENTS_IN_PROGRESS} (shared due diligence assessments provided by ${commonAssessmentsParentService.name})` : ASSESSMENTS_IN_PROGRESS,
                steps,
            };
        } else {
            return {
                completedStepNumber: 4,
                inProgressStepDescription: commonAssessmentsParentService ? `${QUESTIONNAIRES_IN_PROGRESS} (shared due diligence assessments provided by ${commonAssessmentsParentService.name})` : QUESTIONNAIRES_IN_PROGRESS,
                steps,
            };
        }
    } else if (service.assessment_workflow_data.state === AssessmentWorkflowState.EVALUATING_INHERENT_RISK) {
        return {
            completedStepNumber: 3,
            inProgressStepDescription: IRQ_IN_PROGRESS,
            steps,
        };
    } else if (service.finalization_state === FinalizationState.FINALIZED) {
        return {
            completedStepNumber: 2,
            inProgressStepDescription: WORKFLOW_NOT_STARTED,
            steps,
        };
    } else if (thirdParty.finalization_state === FinalizationState.FINALIZED) {
        return {
            completedStepNumber: 1,
            inProgressStepDescription: TPRM_SERVICE_PENDING_FINALIZATION,
            steps,
        };
    } else {
        return {
            completedStepNumber: 0,
            inProgressStepDescription: TPRM_THIRD_PARTY_PENDING_FINALIZATION,
            steps,
        };
    }
};

export const getDueDiligenceWorkflowProgressForServiceForNonTprmUser = (thirdParty: ThirdPartyWithServicesForNonTprmUser, service: ServiceForNonTprmUser, getCommonAssessmentsParentService: (service: ThirdPartyService | ServiceForNonTprmUser) => ThirdPartyService | ServiceForNonTprmUser | undefined): DueDiligenceWorkflowProgress => {
    const steps = [...TPRM_STEPS_FOR_NON_TPRM_USER];
    const commonAssessmentsParentService = getCommonAssessmentsParentService(service);
    const serviceWithAssessments = commonAssessmentsParentService ?? service;

    if (commonAssessmentsParentService) {
        steps[7] = `${TPRM_QUESTIONNAIRES_SUBMITTED} (shared due diligence assessments provided by ${commonAssessmentsParentService.name})`;
        steps[8] = `${ASSESSMENTS_SUBMITTED} (shared due diligence assessments provided by ${commonAssessmentsParentService.name})`;
    }

    if (service.assessment_workflow_data.state === AssessmentWorkflowState.PERFORMING_DUE_DILIGENCE) {
        if (serviceWithAssessments.assessment_workflow_data.all_assessments_completed) {
            return {
                completedStepNumber: 8,
                inProgressStepDescription: FINAL_REVIEW_IN_PROGRESS,
                steps,
            };
        } else if (serviceWithAssessments.assessment_workflow_data.all_questionnaires_completed) {
            return {
                completedStepNumber: 7,
                inProgressStepDescription: commonAssessmentsParentService ? `${ASSESSMENTS_IN_PROGRESS} (shared due diligence assessments provided by ${commonAssessmentsParentService.name})` : ASSESSMENTS_IN_PROGRESS,
                steps,
            };
        } else {
            return {
                completedStepNumber: 6,
                inProgressStepDescription: commonAssessmentsParentService ? `${QUESTIONNAIRES_IN_PROGRESS} (shared due diligence assessments provided by ${commonAssessmentsParentService.name})` : QUESTIONNAIRES_IN_PROGRESS,
                steps,
            };
        }
    } else if (service.assessment_workflow_data.state === AssessmentWorkflowState.EVALUATING_INHERENT_RISK) {
        return {
            completedStepNumber: 5,
            inProgressStepDescription: IRQ_NOT_FINALIZED,
            steps,
        };
    } else if (service.finalization_state === FinalizationState.FINALIZED) {
        return {
            completedStepNumber: 4,
            inProgressStepDescription: WORKFLOW_NOT_STARTED,
            steps,
        };
    } else if (thirdParty.finalization_state === FinalizationState.FINALIZED) {
        return {
            completedStepNumber: 3,
            inProgressStepDescription: TPRM_SERVICE_PENDING_FINALIZATION,
            steps,
        };
    } else if (service.finalization_state === FinalizationState.DRAFT_SUBMITTED_TO_BE_FINALIZED) {
        return {
            completedStepNumber: 2,
            inProgressStepDescription: TPRM_THIRD_PARTY_PENDING_FINALIZATION,
            steps,
        };
    } else if (thirdParty.finalization_state === FinalizationState.DRAFT_SUBMITTED_TO_BE_FINALIZED) {
        return {
            completedStepNumber: 1,
            inProgressStepDescription: TPRM_SERVICE_NOT_SUBMITTED_TO_BE_FINALIZED,
            steps,
        };
    } else {
        return {
            completedStepNumber: 0,
            inProgressStepDescription: TPRM_THIRD_PARTY_NOT_SUBMITTED_TO_BE_FINALIZED,
            steps,
        };
    }
};
